import React, { useState, useEffect } from "react";
import {
  Award,
  RefreshCw,
  HelpCircle,
  Shield,
  Castle,
  ArrowRight,
} from "lucide-react";

export default function SoldierPuzzle() {
  // 使用工厂函数避免对象引用问题
  const getInitialPositions = () => ({
    corners: [4, 4, 4, 4],
    edges: [3, 3, 3, 3],
    tower: 0,
  });

  const [positions, setPositions] = useState(getInitialPositions());
  const [message, setMessage] = useState(
    "欢迎来到守卫城！试着解决守护塔的难题吧！"
  );
  const [solved, setSolved] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [soldierCounts, setSoldierCounts] = useState({
    row1: 11,
    row3: 11,
    col1: 11,
    col3: 11,
  });
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // 计算每条大街和小巷的士兵数
  useEffect(() => {
    // 计算第一行大街士兵数: 左上角 + 上中点 + 右上角
    const row1Count =
      positions.corners[0] + positions.edges[0] + positions.corners[1];
    // 计算第三行大街士兵数: 左下角 + 下中点 + 右下角
    const row3Count =
      positions.corners[2] + positions.edges[2] + positions.corners[3];
    // 计算第一列小巷士兵数: 左上角 + 左中点 + 左下角
    const col1Count =
      positions.corners[0] + positions.edges[3] + positions.corners[2];
    // 计算第三列小巷士兵数: 右上角 + 右中点 + 右下角
    const col3Count =
      positions.corners[1] + positions.edges[1] + positions.corners[3];

    setSoldierCounts({
      row1: row1Count,
      row3: row3Count,
      col1: col1Count,
      col3: col3Count,
    });

    // 检查是否解决谜题 - 每条街道和小巷都有11个士兵，并且守护塔有4名士兵
    const puzzleSolved =
      row1Count === 11 &&
      row3Count === 11 &&
      col1Count === 11 &&
      col3Count === 11 &&
      positions.tower === 4;

    setSolved(puzzleSolved);

    if (puzzleSolved) {
      setMessage("太棒了！你成功解决了守卫城的难题！守护塔获得了所需的士兵！");
    }
  }, [positions, updateTrigger]);

  // 处理士兵调动
  const handlePositionClick = (type, index) => {
    if (selectedPosition) {
      // 如果点击的是同一个位置，取消选择
      if (
        selectedPosition.type === type &&
        (type === "tower" || selectedPosition.index === index)
      ) {
        setSelectedPosition(null);
        setMessage("已取消选择");
        return;
      }

      // 已经选择过一个位置，现在是要移动士兵
      const newPositions = { ...positions };

      // 如果目标是守护塔
      if (type === "tower") {
        // 检查守护塔是否已满
        if (positions.tower >= 4) {
          setMessage("守护塔已满，无法再添加士兵！");
          setSelectedPosition(null);
          return;
        }

        // 从选择的位置调走一个士兵
        if (selectedPosition.type === "corners") {
          // 检查是否有士兵可调
          if (positions.corners[selectedPosition.index] <= 0) {
            setMessage("该位置没有士兵可调动！");
            setSelectedPosition(null);
            return;
          }

          newPositions.corners[selectedPosition.index]--;
        } else if (selectedPosition.type === "edges") {
          // 检查是否有士兵可调
          if (positions.edges[selectedPosition.index] <= 0) {
            setMessage("该位置没有士兵可调动！");
            setSelectedPosition(null);
            return;
          }

          newPositions.edges[selectedPosition.index]--;
        }

        // 增加守护塔的士兵
        newPositions.tower++;
        setMessage("你将一名士兵调到了守护塔！");
      } else if (selectedPosition.type === "tower") {
        // 从守护塔中调出士兵
        if (newPositions.tower > 0) {
          newPositions.tower--;

          // 向目标位置增加一个士兵
          if (type === "corners") {
            newPositions.corners[index]++;
          } else if (type === "edges") {
            newPositions.edges[index]++;
          }

          setMessage("你将一名士兵从守护塔调出！");
        } else {
          setMessage("守护塔没有士兵可调出！");
          setSelectedPosition(null);
          return;
        }
      } else {
        // 检查选择的位置是否有士兵
        let hasSelectedSoldier = false;
        if (selectedPosition.type === "corners") {
          hasSelectedSoldier = positions.corners[selectedPosition.index] > 0;
        } else if (selectedPosition.type === "edges") {
          hasSelectedSoldier = positions.edges[selectedPosition.index] > 0;
        }

        if (!hasSelectedSoldier) {
          setMessage("选择的位置没有士兵可调动！");
          setSelectedPosition(null);
          return;
        }

        // 从选择的位置调走一个士兵
        if (selectedPosition.type === "corners") {
          newPositions.corners[selectedPosition.index]--;
        } else if (selectedPosition.type === "edges") {
          newPositions.edges[selectedPosition.index]--;
        }

        // 向目标位置增加一个士兵
        if (type === "corners") {
          newPositions.corners[index]++;
        } else if (type === "edges") {
          newPositions.edges[index]++;
        }

        setMessage("你调动了一名士兵！");
      }

      setPositions(newPositions);
      setSelectedPosition(null);
      // 强制触发状态更新
      setUpdateTrigger((prev) => prev + 1);
    } else {
      // 选择一个位置准备调动士兵
      let soldierCount = 0;

      if (type === "tower") {
        soldierCount = positions.tower;
      } else if (type === "corners") {
        soldierCount = positions.corners[index];
      } else if (type === "edges") {
        soldierCount = positions.edges[index];
      }

      if (soldierCount > 0) {
        setSelectedPosition({ type, index });
        setMessage("已选择一处士兵，点击另一处位置放置他");
      } else {
        setMessage("该位置没有士兵可调动！");
      }
    }
  };

  // 重置游戏
  const resetGame = () => {
    setPositions(getInitialPositions());
    setSelectedPosition(null);
    setMessage("游戏已重置！试着解决守卫城的难题吧！");
    setSolved(false);
    setShowHint(false);
    setUpdateTrigger(0);
  };

  // 显示提示
  const toggleHint = () => {
    setShowHint(!showHint);
    if (!showHint) {
      setMessage("提示：思考哪些位置的士兵同时属于两条街道！");
    } else {
      setMessage("继续尝试吧！");
    }
  };

  // 渲染士兵网格
  const renderGrid = () => {
    const gridSize = 320;
    const cellSize = gridSize / 3;

    // 位置坐标映射
    const positionCoords = {
      // 顶点位置 [左上, 右上, 左下, 右下]
      corners: [
        { x: 0, y: 0 },
        { x: 2, y: 0 },
        { x: 0, y: 2 },
        { x: 2, y: 2 },
      ],
      // 边中点位置 [上中, 右中, 下中, 左中]
      edges: [
        { x: 1, y: 0 },
        { x: 2, y: 1 },
        { x: 1, y: 2 },
        { x: 0, y: 1 },
      ],
    };

    // 外部容器的padding，用于确保内容居中
    const containerPadding = 60;

    return (
      <div
        className="relative bg-blue-50 rounded-lg p-4 shadow-md mx-auto"
        style={{
          width: gridSize + containerPadding * 2,
          height: gridSize + containerPadding * 2,
        }}
      >
        {/* 创建一个内部定位容器，确保所有内容居中 */}
        <div
          className="absolute"
          style={{
            top: containerPadding,
            left: containerPadding,
            width: gridSize,
            height: gridSize,
          }}
        >
          {/* 横向大街背景 */}
          <div
            className="absolute"
            style={{
              top: 0,
              left: 0,
              width: gridSize,
              height: cellSize,
              backgroundColor: "rgba(252, 211, 77, 0.2)",
              border: "2px dashed #f59e0b",
            }}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 font-bold text-amber-600 text-xs">
              第一横街
            </div>
          </div>

          <div
            className="absolute"
            style={{
              top: cellSize * 2,
              left: 0,
              width: gridSize,
              height: cellSize,
              backgroundColor: "rgba(252, 211, 77, 0.2)",
              border: "2px dashed #f59e0b",
            }}
          >
            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 font-bold text-amber-600 text-xs">
              第二横街
            </div>
          </div>

          {/* 纵向小巷背景 */}
          <div
            className="absolute"
            style={{
              left: 0,
              top: 0,
              width: cellSize,
              height: gridSize,
              backgroundColor: "rgba(96, 165, 250, 0.2)",
              border: "2px dashed #3b82f6",
            }}
          >
            <div
              className="absolute -left-6 top-1/2 transform -translate-y-1/2 font-bold text-blue-600 text-xs"
              style={{ writingMode: "vertical-rl", textOrientation: "upright" }}
            >
              第一竖街
            </div>
          </div>

          <div
            className="absolute"
            style={{
              left: cellSize * 2,
              top: 0,
              width: cellSize,
              height: gridSize,
              backgroundColor: "rgba(96, 165, 250, 0.2)",
              border: "2px dashed #3b82f6",
            }}
          >
            <div
              className="absolute -right-6 top-1/2 transform -translate-y-1/2 font-bold text-blue-600 text-xs"
              style={{ writingMode: "vertical-rl", textOrientation: "upright" }}
            >
              第二竖街
            </div>
          </div>

          {/* 网格背景 */}
          <div
            className="absolute"
            style={{ top: 0, left: 0, width: gridSize, height: gridSize }}
          >
            <div className="grid grid-cols-3 grid-rows-3 w-full h-full">
              {Array.from({ length: 9 }).map((_, idx) => (
                <div
                  key={idx}
                  className={`border border-gray-200 ${
                    idx === 4 ? "bg-gray-100" : ""
                  }`}
                ></div>
              ))}
            </div>
          </div>

          {/* 渲染守护塔 */}
          <div
            className={`absolute flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-all rounded-lg ${
              positions.tower === 4
                ? "bg-green-100 border-2 border-green-400"
                : selectedPosition && selectedPosition.type === "tower"
                ? "bg-blue-200 border-2 border-blue-400"
                : ""
            }`}
            style={{
              width: cellSize,
              height: cellSize,
              top: cellSize,
              left: cellSize,
            }}
            onClick={() => handlePositionClick("tower", 0)}
          >
            <Castle
              size={40}
              className={
                positions.tower === 4 ? "text-green-700" : "text-purple-700"
              }
            />
            <div
              className={`text-xs font-bold mt-1 ${
                positions.tower === 4 ? "text-green-900" : "text-purple-900"
              }`}
            >
              守护塔
            </div>

            {/* 显示守护塔中的士兵 */}
            <div className="flex items-center mt-1">
              {Array.from({ length: positions.tower }).map((_, i) => (
                <Shield
                  key={`tower-soldier-${i}`}
                  size={14}
                  className={`mx-0.5 ${
                    positions.tower === 4 ? "text-green-600" : "text-purple-600"
                  }`}
                  fill={positions.tower === 4 ? "#dcfce7" : "#e9d5ff"}
                />
              ))}
            </div>
            <div
              className={`text-xs font-bold mt-1 ${
                positions.tower === 4 ? "text-green-800" : "text-purple-800"
              }`}
            >
              {positions.tower}/4
              {positions.tower === 4 && <span className="text-xs ml-1">✓</span>}
            </div>
          </div>

          {/* 渲染士兵 - 顶点位置 */}
          {positions.corners.map((count, index) => (
            <div
              key={`corner-${index}`}
              className={`absolute flex items-center justify-center cursor-pointer transition-all ${
                selectedPosition &&
                selectedPosition.type === "corners" &&
                selectedPosition.index === index
                  ? "bg-blue-200 rounded-lg border-2 border-blue-400"
                  : ""
              }`}
              style={{
                width: cellSize,
                height: cellSize,
                top: positionCoords.corners[index].y * cellSize,
                left: positionCoords.corners[index].x * cellSize,
              }}
              onClick={() => handlePositionClick("corners", index)}
            >
              {Array.from({ length: count }).map((_, i) => (
                <Shield
                  key={`corner-${index}-soldier-${i}`}
                  size={16}
                  className="text-red-600 m-1"
                  fill="#fed7d7"
                />
              ))}
              <div className="absolute text-xs font-bold text-red-800">
                {count}
              </div>
            </div>
          ))}

          {/* 渲染士兵 - 边缘中点位置 */}
          {positions.edges.map((count, index) => (
            <div
              key={`edge-${index}`}
              className={`absolute flex items-center justify-center cursor-pointer transition-all ${
                selectedPosition &&
                selectedPosition.type === "edges" &&
                selectedPosition.index === index
                  ? "bg-blue-200 rounded-lg border-2 border-blue-400"
                  : ""
              }`}
              style={{
                width: cellSize,
                height: cellSize,
                top: positionCoords.edges[index].y * cellSize,
                left: positionCoords.edges[index].x * cellSize,
              }}
              onClick={() => handlePositionClick("edges", index)}
            >
              {Array.from({ length: count }).map((_, i) => (
                <Shield
                  key={`edge-${index}-soldier-${i}`}
                  size={16}
                  className="text-blue-600 m-1"
                  fill="#d7e3fe"
                />
              ))}
              <div className="absolute text-xs font-bold text-blue-800">
                {count}
              </div>
            </div>
          ))}
        </div>

        {/* 横向大街统计 */}
        <div
          key={`row1-${soldierCounts.row1}-${positions.tower}-${updateTrigger}`}
          className="absolute flex items-center"
          style={{
            top: containerPadding + cellSize / 2,
            right: containerPadding / 20,
            transform: "translateY(-50%)",
          }}
        >
          <ArrowRight size={16} className="text-amber-600 mr-1" />
          <div
            className={`text-lg font-bold px-2 py-1 rounded-lg ${
              soldierCounts.row1 === 11
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {soldierCounts.row1}
            {soldierCounts.row1 === 11 && (
              <span className="text-xs ml-1">✓</span>
            )}
          </div>
        </div>

        <div
          key={`row3-${soldierCounts.row3}-${positions.tower}-${updateTrigger}`}
          className="absolute flex items-center"
          style={{
            top: containerPadding + cellSize * 2.5,
            right: containerPadding / 20,
            transform: "translateY(-50%)",
          }}
        >
          <ArrowRight size={16} className="text-amber-600 mr-1" />
          <div
            className={`text-lg font-bold px-2 py-1 rounded-lg ${
              soldierCounts.row3 === 11
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {soldierCounts.row3}
            {soldierCounts.row3 === 11 && (
              <span className="text-xs ml-1">✓</span>
            )}
          </div>
        </div>

        {/* 纵向小巷统计 */}
        <div
          key={`col1-${soldierCounts.col1}-${positions.tower}-${updateTrigger}`}
          className="absolute flex items-center"
          style={{
            left: containerPadding + cellSize / 2,
            top: containerPadding / 2 - 10,
            transform: "translateX(-50%)",
          }}
        >
          <ArrowRight
            size={16}
            className="text-blue-600 transform rotate-90 mr-1"
          />
          <div
            className={`text-lg font-bold px-2 py-1 rounded-lg ${
              soldierCounts.col1 === 11
                ? "bg-green-100 text-green-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {soldierCounts.col1}
            {soldierCounts.col1 === 11 && (
              <span className="text-xs ml-1">✓</span>
            )}
          </div>
        </div>

        <div
          key={`col3-${soldierCounts.col3}-${positions.tower}-${updateTrigger}`}
          className="absolute flex items-center"
          style={{
            left: containerPadding + cellSize * 2.5,
            top: containerPadding / 2 - 10,
            transform: "translateX(-50%)",
          }}
        >
          <ArrowRight
            size={16}
            className="text-blue-600 transform rotate-90 mr-1"
          />
          <div
            className={`text-lg font-bold px-2 py-1 rounded-lg ${
              soldierCounts.col3 === 11
                ? "bg-green-100 text-green-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {soldierCounts.col3}
            {soldierCounts.col3 === 11 && (
              <span className="text-xs ml-1">✓</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl min-h-screen">
      <h1 className="text-2xl font-bold text-indigo-700 mb-2">守卫难题</h1>

      <div className="bg-white p-4 rounded-lg shadow-md mb-4 max-w-lg text-left">
        <p className="text-gray-700">
          守卫城有4条街道（2横、2纵），中间是守护塔。每条街道原本都安排了11名士兵守卫。
        </p>
        <p className="text-gray-700">
          一天，守卫塔需要从城池4个顶角，各调走一名士兵前往守护塔支援。但城规要求：每条街道的守卫人数必须保持11人不变。
        </p>
        <p className="text-gray-700 font-bold">你能帮助解决这个难题吗？</p>
      </div>

      <div
        className={`p-4 mb-4 rounded-lg ${
          solved ? "bg-green-100 border-2 border-green-500" : "bg-white"
        } shadow-md`}
      >
        {renderGrid()}
      </div>

      <div className="mb-4 text-center w-full max-w-lg">
        <div
          className={`p-3 rounded-lg ${
            solved ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
          }`}
        >
          {message}
        </div>

        {showHint && (
          <div className="mt-2 p-3 bg-yellow-100 text-yellow-800 rounded-lg">
            提示：试一试点击方格，移动守卫位置，观察各街道人数如何改变，思考为什么？
          </div>
        )}
      </div>

      <div className="flex space-x-4">
        <button
          className="flex items-center px-4 py-2 bg-purple-500 text-white rounded-full font-medium hover:bg-purple-600 transition-colors"
          onClick={toggleHint}
        >
          <HelpCircle size={16} className="mr-1" />
          {showHint ? "隐藏提示" : "显示提示"}
        </button>

        <button
          className="flex items-center px-4 py-2 bg-yellow-500 text-white rounded-full font-medium hover:bg-yellow-600 transition-colors"
          onClick={resetGame}
        >
          <RefreshCw size={16} className="mr-1" />
          重新开始
        </button>
      </div>

      {solved && (
        <div className="mt-6 flex items-center justify-center">
          <Award className="h-8 w-8 text-yellow-500 mr-2" />
          <span className="text-xl font-bold text-yellow-600">
            恭喜你！你是守卫城的救星！
          </span>
        </div>
      )}
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
}
